<template>
  <v-container fluid>
    <v-row class="pa-1">
      <v-col lg="7" md="8" sm="8" xs="12">
        <v-form class="main-row" ref="form" v-model="valid" lazy-validation>
          <div class="d-flex">
            <span class="text-subtitle-2 font-weight-bold red--text">
              {{$t("select trending type")}}
            </span>
          </div>
          <div class="d-flex">
            <v-radio-group v-model="addType" row>
              <v-radio color="black" value="video">
                <template v-slot:label>
                  <span class="text-caption">{{$t("video")}}</span>
                </template>
              </v-radio>
              <v-radio color="black" value="channel">
                <template v-slot:label>
                  <span class="text-caption">{{$t("channel")}}</span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>

          <span class="text-subtitle-2 font-weight-bold">
            {{ addType === 'video' ? $t("the title of the video") : $t("channel") }}
          </span>
          <div class="d-flex py-2">
            <v-autocomplete
                v-if="addType === 'video'"
                v-model="selectedVideo"
                :loading="videosLoading"
                :items="videos"
                :search-input.sync="searchVideo"
                class="select-input"
                hide-no-data
                hide-details
                outlined
                dense
                return-object
                item-text="title"
                item-value="id"
                :placeholder="$t('type to search')"
            />
            <v-autocomplete
                v-else
                v-model="selectedChannel"
                :loading="channelsLoading"
                :items="channels"
                :search-input.sync="searchChannel"
                class="select-input"
                hide-no-data
                hide-details
                outlined
                dense
                return-object
                item-text="name"
                item-value="id"
                :placeholder="$t('type to search')"
            />
            <div class="mx-5 mark-img">
              <v-icon v-if="addType === 'video' ? selectedVideo : selectedChannel" size="30" color="black"
              >mdi-check-bold</v-icon
              >
            </div>
          </div>

          <simple-video-item v-if="addType === 'video' && selectedVideo" :video="selectedVideo" />
          <simple-channel-item v-if="addType === 'channel' && selectedChannel" :channel="selectedChannel" />

          <template v-if="addType === 'video'">
            <span class="text-subtitle-2 font-weight-bold">{{$t("location")}}</span>
            <div class="d-flex py-2">
              <v-select
                  class="select-input"
                  v-model="selectedPosition"
                  :items="positions"
                  :label="$t('choose')"
                  dense
                  outlined
                  single-line
              />
              <div class="mx-5 mark-img">
                <v-icon v-if="selectedPosition" size="30" color="black"
                >mdi-check-bold</v-icon
                >
              </div>
            </div>
          </template>

          <template v-else>
            <span class="text-subtitle-2 font-weight-bold">{{$t("number of videos")}}</span>
            <div class="d-flex py-2">
              <v-select
                  class="select-input"
                  v-model="selectedVideoCount"
                  :items="videoCounts"
                  :label="$t('choose')"
                  dense
                  outlined
                  single-line
              />
              <div class="mx-5 mark-img">
                <v-icon v-if="selectedVideoCount" size="30" color="black"
                >mdi-check-bold</v-icon
                >
              </div>
            </div>
          </template>

          <span class="text-subtitle-2 font-weight-bold">{{$t("expires at")}}</span>
          <div class="d-flex">
            <v-menu
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="dateFormatted"
                    class="date-input"
                    append-icon="mdi-menu-down"
                    persistent-hint
                    dense
                    outlined
                    single-line
                    v-bind="attrs"
                    @blur="date = parseDate(dateFormatted)"
                    v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="date"
                  no-title
                  @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
            <v-menu
                ref="menu"
                v-model="timeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="time"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="time"
                    class="date-input"
                    append-icon="mdi-menu-down"
                    readonly
                    dense
                    single-line
                    outlined
                    v-bind="attrs"
                    v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                  v-if="timeMenu"
                  v-model="time"
                  full-width
                  @click:minute="$refs.menu.save(time)"
              ></v-time-picker>
            </v-menu>
            <v-select
                v-model="selectedTimezone"
                :items="timezoneLits"
                item-text="name"
                item-value="zone"
                :label="$t('choose')"
                dense
                outlined
                single-line
                return-object
                class="timezone-input"
            />
          </div>

          <div class="d-flex py-2">
            <div class="d-flex justify-end mt-4">
              <v-btn
                  tile
                  color="#616161"
                  width="140"
                  class="white--text"
                  @click="onCancel()"
              >{{ $t("cancel") }}</v-btn>
              <v-btn
                  tile
                  color="#388E3C"
                  width="140"
                  class="white--text mx-2"
                  @click="submit"
                  :loading="loading"
                  :disabled="loading || !formValid">
                {{$t("routine")}}!
              </v-btn>
            </div>
            <div class="mx-5 mark-img" />
          </div>
        </v-form>
      </v-col>

      <v-col lg="5" md="4" sm="4" xs="12">
        <div class="list-panel pt-14">
          <div v-if="addType === 'video'" class="list-scroll">
            <div v-if="pinsLoading" class="d-flex justify-center pa-4">
              <v-progress-circular indeterminate color="black" />
            </div>
            <div
                v-for="item in pins"
                :key="item.trending_manual_pin.id"
                class="list-item"
                :class="{ 'list-item--selected': editingId === item.trending_manual_pin.id }"
                @click="selectPin(item)"
            >
              <simple-video-item
                  :video="item"
                  show-delete
                  @delete="confirmDelete('pin', item.trending_manual_pin.id, item.title)"
              >
                <template #badge>
                  <font-awesome-layers full-width class="fa-2x">
                    <font-awesome-icon icon="lock" style="color: red" />
                    <font-awesome-layers-text
                        v-if="item.trending_manual_pin.position"
                        :style="{ color: 'white' }"
                        transform="down-3 shrink-10"
                        :value="item.trending_manual_pin.position"
                    />
                  </font-awesome-layers>
                </template>
              </simple-video-item>
            </div>
          </div>

          <div v-else class="list-scroll">
            <div v-if="boostsLoading" class="d-flex justify-center pa-4">
              <v-progress-circular indeterminate color="black" />
            </div>
            <div
                v-for="item in boosts"
                :key="item.trending_channel_boost.id"
                class="list-item pa-2"
                :class="{ 'list-item--selected': editingId === item.trending_channel_boost.id }"
                @click="selectBoost(item)"
            >
              <simple-channel-item
                  :channel="item"
                  show-delete
                  @delete="confirmDelete('boost', item.trending_channel_boost.id, item.name)"
              />
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="deleteDialog" max-width="480">
      <v-card class="pa-10">
        <v-card-title class="text-h6 font-weight-bold pa-0 mb-5">
          אתה בטוח?
        </v-card-title>
        <span class="text-subtitle-2">{{ deleteTarget && deleteTarget.label }}</span>
        <v-card-actions class="d-flex justify-space-between px-0 mt-10">
          <v-btn
              class="white--text flex-grow-1"
              color="#616161"
              tile
              x-large
              @click="deleteDialog = false"
          >{{ $t('cancel') }}</v-btn>
          <v-btn
              class="white--text flex-grow-1"
              color="#ff0000"
              tile
              x-large
              @click="onDeleteConfirmed()"
              :loading="deleting"
              :disabled="deleting"
          >{{ $t('delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/api/api";
import moment from 'moment';
import timezones from "../../config/timezone.js";

export default {
  beforeRouteEnter(to, from, next) {
    api.getSystemSetting('trending_algorithm')
      .then((res) => {
        if (res.data.value === 'v2') {
          next();
        } else {
          next('/trending');
        }
      })
      .catch(() => {
        next('/trending');
      });
  },
  data() {
    return {
      valid: true,
      loading: false,
      addType: "video", // 'video' | 'channel'

      editingId: null,

      pins: [],
      pinsLoading: false,
      boosts: [],
      boostsLoading: false,

      deleteDialog: false,
      deleteTarget: null, // { type: 'pin' | 'boost', id, label }
      deleting: false,

      // addType === 'video'
      searchVideo: null,
      videosLoading: false,
      videos: [],
      selectedVideo: null,
      positions: [...Array(25).keys()].map(p => p + 1),
      selectedPosition: undefined,

      // addType === 'channel'
      searchChannel: null,
      channelsLoading: false,
      channels: [],
      selectedChannel: null,
      videoCounts: [1, 2, 3],
      selectedVideoCount: undefined,

      // expires_at
      time: null,
      date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      dateFormatted: this.formatDate((new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)),
      timeMenu: false,
      dateMenu: false,
      timezoneLits: timezones,
      selectedTimezone: timezones[0],
    };
  },

  watch: {
    date() {
      this.dateFormatted = this.formatDate(this.date)
    },
    searchVideo(val) {
      if (this.selectedVideo && val === this.selectedVideo.title) {
        return;
      }
      if (this.videoDebounce) {
        clearTimeout(this.videoDebounce);
        this.videoDebounce = null;
      }
      this.videoDebounce = setTimeout(() => {
        this.queryVideos(val);
      }, 1000);
    },
    searchChannel(val) {
      if (this.selectedChannel && val === this.selectedChannel.name) {
        return;
      }
      if (this.channelDebounce) {
        clearTimeout(this.channelDebounce);
        this.channelDebounce = null;
      }
      this.channelDebounce = setTimeout(() => {
        this.queryChannels(val);
      }, 1000);
    },
    addType() {
      this.resetForm();
    }
  },

  computed: {
    formValid() {
      if (!this.expiresAtValid()) return false;

      if (this.addType === 'video') {
        return !!this.selectedVideo && !!this.selectedPosition;
      }

      return !!this.selectedChannel && !!this.selectedVideoCount;
    },
  },

  mounted() {
    this.fetchPins();
    this.fetchBoosts();
  },

  methods: {
    formatDate(date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },
    parseDate(date) {
      if (!date) return null

      const [month, day, year] = date.split('/')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },
    getExpiresAt() {
      const strISO8061 = moment.tz(`${this.date} ${this.time}`, this.selectedTimezone.zone).toISOString();
      return new Date(strISO8061);
    },
    expiresAtValid() {
      if (!this.date || !this.time) return false;
      if (new Date() > this.getExpiresAt()) return false;

      return true;
    },
    async queryVideos(val) {
      this.videosLoading = true;
      const params = {
        term: val,
        limit: 20,
        offset: 0,
      };
      api.getChannelVideos(params, 'all')
          .then((res) => {
            const publishedVideos = res.data.videos.filter((v) => v.status === 'published');

            this.videos = this.selectedVideo ? [this.selectedVideo, ...publishedVideos] : publishedVideos;
            this.videosLoading = false;
          })
          .catch((error) => {
            this.videosLoading = false;
            console.log(error);
          });
    },
    async queryChannels(val) {
      this.channelsLoading = true;
      api.searchChannels(val)
          .then((res) => {
            this.channels = this.selectedChannel ? [this.selectedChannel, ...res.data.items] : res.data.items;
            this.channelsLoading = false;
          })
          .catch((error) => {
            this.channelsLoading = false;
            console.log(error);
          });
    },
    fetchPins() {
      this.pinsLoading = true;
      api.getTrendingManualPins({ limit: 100 })
          .then((res) => {
            this.pins = res.data.trending_manual_pins;
            this.pinsLoading = false;
          })
          .catch((err) => {
            console.log(err);
            this.pinsLoading = false;
          });
    },
    fetchBoosts() {
      this.boostsLoading = true;
      api.getTrendingChannelBoosts({ limit: 100 })
          .then((res) => {
            this.boosts = res.data.trending_channel_boosts;
            this.boostsLoading = false;
          })
          .catch((err) => {
            console.log(err);
            this.boostsLoading = false;
          });
    },
    setExpiresAtFields(isoString) {
      const target = isoString
          ? moment(isoString).tz(this.selectedTimezone.zone)
          : moment().tz(this.selectedTimezone.zone);

      this.date = target.format('YYYY-MM-DD');
      this.time = target.format('HH:mm');
    },
    selectPin(item) {
      const pinId = item.trending_manual_pin.id;

      if (this.editingId === pinId) {
        this.resetForm();
        return;
      }

      this.addType = 'video';
      this.editingId = pinId;
      this.selectedVideo = item;
      this.videos = [item];
      this.selectedPosition = item.trending_manual_pin.position;
      this.setExpiresAtFields(item.trending_manual_pin.expires_at);
    },
    selectBoost(item) {
      const boostId = item.trending_channel_boost.id;

      if (this.editingId === boostId) {
        this.resetForm();
        return;
      }

      this.addType = 'channel';
      this.editingId = boostId;
      this.selectedChannel = item;
      this.channels = [item];
      this.selectedVideoCount = item.trending_channel_boost.video_count;
      this.setExpiresAtFields(item.trending_channel_boost.expires_at);
    },
    resetForm() {
      this.editingId = null;
      this.selectedVideo = null;
      this.selectedPosition = undefined;
      this.selectedChannel = null;
      this.selectedVideoCount = undefined;
      this.setExpiresAtFields();
    },
    confirmDelete(type, id, label) {
      this.deleteTarget = { type, id, label };
      this.deleteDialog = true;
    },
    onDeleteConfirmed() {
      this.deleting = true;
      const { type, id } = this.deleteTarget;
      const request = type === 'pin'
        ? api.deleteTrendingManualPin(id)
        : api.deleteTrendingChannelBoost(id);

      request
        .then(() => {
          this.deleting = false;
          this.deleteDialog = false;
          this.deleteTarget = null;

          if (this.editingId === id) {
            this.resetForm();
          }

          if (type === 'pin') {
            this.fetchPins();
          } else {
            this.fetchBoosts();
          }
        })
        .catch((err) => {
          console.log(err);
          this.deleting = false;
        });
    },
    submit() {
      this.loading = true;

      if (this.addType === 'video') {
        this.createTrendingManualPin();
      } else {
        this.createTrendingChannelBoost();
      }
    },
    createTrendingManualPin() {
      const params = {
        video_id: this.selectedVideo.id,
        position: this.selectedPosition,
        expires_at: this.getExpiresAt().toISOString(),
      };

      api.postTrendingManualPin(params)
          .then(() => {
            this.loading = false;
            this.resetForm();
            this.fetchPins();
          })
          .catch((err) => {
            console.log(err);
            this.loading = false;
          });
    },
    createTrendingChannelBoost() {
      const params = {
        channel_id: this.selectedChannel.id,
        video_count: this.selectedVideoCount,
        expires_at: this.getExpiresAt().toISOString(),
      };

      api.postTrendingChannelBoost(params)
          .then(() => {
            this.loading = false;
            this.resetForm();
            this.fetchBoosts();
          })
          .catch((err) => {
            console.log(err);
            this.loading = false;
          });
    },
    onCancel() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.main-row {
  flex: 1;
}
.select-input {
  border-radius: 0px;
}
.date-input {
  border-radius: 0px;
  font-size: 12px;
  min-width: 100px;
  max-width: 150px;
  margin-left: 20px !important;
}
.mark-img {
  width: 70px;
  justify-content: center;
  display: flex;
  align-items: flex-start;
}
.timezone-input {
  border-radius: 0px;
  max-width: 200px;
  font-size: 12px;
  margin-left: 20px !important;
}
.list-scroll {
  max-height: 640px;
  overflow-y: auto;
  margin-top: 8px;
}
.list-item {
  position: relative;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid transparent;
}
.list-item--selected {
  background-color: #bdbdbd;
  border-color: #bdbdbd;
}
</style>
